import React, { useState } from 'react';
import { Copy, ExternalLink, FileText } from 'lucide-react';
import ModalShell from '../ModalShell';
import { buildDocumentShareUrl, parseDocumentShareParams } from '../../utils/documentLinks';
import { getApiUrl } from '../../utils/api';
import { showToast } from '../../utils/toast';
import { DEFAULT_API_TOKEN } from '../../constants/portals';

function DetailRow({ label, value }) {
  if (!value) return null;

  return (
    <div className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 dark:border-gray-700 dark:bg-gray-900/40">
      <p className="text-[9px] font-black uppercase tracking-widest text-gray-400 dark:text-gray-500">{label}</p>
      <p className="mt-1 break-words text-sm font-bold text-gray-800 dark:text-gray-100">{value}</p>
    </div>
  );
}

export default function DocumentLinkModal({ onClose, search }) {
  const [isCopying, setIsCopying] = useState(false);
  const document = parseDocumentShareParams(search);
  const shareUrl = buildDocumentShareUrl({
    officialUrl: document.officialUrl,
    nome: document.nome,
    portalName: document.portal,
    tipo: document.tipo,
    data: document.data,
    sistema: document.sistema,
    description: document.description,
    discontinued: document.discontinued,
    replacedByName: document.replacedByName,
    replacedByUrl: document.replacedByUrl
  });

  const copyShareUrl = async () => {
    setIsCopying(true);

    try {
      let urlToCopy = shareUrl;
      const response = await fetch(getApiUrl('document-links'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          ...(DEFAULT_API_TOKEN ? { 'x-app-token': DEFAULT_API_TOKEN } : {})
        },
        body: JSON.stringify({
          document: {
            officialUrl: document.officialUrl,
            nome: document.nome,
            portalName: document.portal,
            tipo: document.tipo,
            data: document.data,
            sistema: document.sistema,
            description: document.description,
            discontinued: document.discontinued,
            replacedByName: document.replacedByName,
            replacedByUrl: document.replacedByUrl
          }
        })
      });

      if (response.ok) {
        const payload = await response.json();
        urlToCopy = payload.shortUrl || urlToCopy;
      }

      await navigator.clipboard.writeText(urlToCopy);
      showToast('Link do Monitor Fiscal copiado.', { type: 'success' });
    } catch {
      try {
        await navigator.clipboard.writeText(shareUrl);
        showToast('Link do Monitor Fiscal copiado.', { type: 'success' });
      } catch {
        showToast('Não foi possível copiar o link.', { type: 'error' });
      }
    } finally {
      setIsCopying(false);
    }
  };

  return (
    <ModalShell
      icon={FileText}
      maxWidthClass="max-w-2xl"
      onClose={onClose}
      subtitle={document.portal || document.sistema || 'Documento técnico fiscal'}
      themeClass="bg-gradient-to-r from-sky-700 to-teal-700"
      title={document.nome || 'Documento técnico'}
    >
      <div className="flex min-h-0 flex-1 flex-col">
        <div className="overflow-y-auto p-4 sm:p-6">
          {document.discontinued && (
            <div className="mb-4 rounded-xl border border-red-200 bg-red-50 p-4 dark:border-red-900/40 dark:bg-red-900/10">
              <div className="flex items-start gap-3">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-bold text-red-800 dark:text-red-200">
                    Descontinuado
                  </p>
                  <p className="mt-1 text-xs leading-5 text-red-700/80 dark:text-red-200/80">
                    Este documento foi substituído por{' '}
                    <a href={document.replacedByUrl} target="_blank" rel="noopener noreferrer" className="font-bold underline hover:text-red-900 dark:hover:text-red-100">
                      {document.replacedByName || 'uma nova versão'}
                    </a>.
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="grid gap-3 sm:grid-cols-2">
            <DetailRow label="Portal" value={document.portal} />
            <DetailRow label="Sistema" value={document.sistema} />
            <DetailRow label="Tipo" value={document.tipo} />
            <DetailRow label="Data" value={document.data} />
          </div>

          {document.description ? (
            <div className="mt-4 rounded-2xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
              <p className="text-[10px] font-black uppercase tracking-widest text-gray-400 dark:text-gray-500">Resumo</p>
              <p className="mt-2 whitespace-pre-wrap break-words text-sm leading-6 text-gray-700 dark:text-gray-200">
                {document.description}
              </p>
            </div>
          ) : null}

          {!document.description ? (
            <div className="mt-4 rounded-2xl border border-gray-200 bg-gray-50 p-4 text-sm leading-6 text-gray-500 dark:border-gray-700 dark:bg-gray-900/40 dark:text-gray-400">
              Resumo não disponível para este documento.
            </div>
          ) : null}
        </div>

        <div className="flex shrink-0 items-center justify-end gap-2 border-t border-gray-100 bg-white p-4 dark:border-gray-700 dark:bg-gray-800 sm:px-6">
          <button
            type="button"
            onClick={copyShareUrl}
            disabled={isCopying}
            className="inline-flex h-11 w-11 items-center justify-center rounded-xl border border-gray-200 bg-white text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-wait disabled:opacity-60 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200 dark:hover:bg-gray-700"
            title="Copiar link do Monitor Fiscal"
            aria-label="Copiar link do Monitor Fiscal"
          >
            <Copy className="h-4 w-4" />
          </button>

          {document.officialUrl ? (
            <a
              href={document.officialUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-sky-700 px-4 text-[11px] font-black uppercase tracking-widest text-white transition-colors hover:bg-sky-800"
            >
              <ExternalLink className="h-4 w-4" />
              Link SEFAZ
            </a>
          ) : (
            <button
              type="button"
              disabled
              className="inline-flex h-11 cursor-not-allowed items-center justify-center gap-2 rounded-xl bg-gray-300 px-4 text-[11px] font-black uppercase tracking-widest text-white dark:bg-gray-700"
            >
              <ExternalLink className="h-4 w-4" />
              Link SEFAZ
            </button>
          )}
        </div>
      </div>
    </ModalShell>
  );
}
